export type NormalizedCookie = Record<string, unknown> & {
  name: string;
  value: string;
  domain: string;
  path: string;
  secure: boolean;
  httpOnly: boolean;
  sameSite: "no_restriction" | "lax" | "strict" | "unspecified";
};

export interface ParsedCookieAccount {
  /** Optional export label. The upstream identity probe remains authoritative. */
  label?: string;
  cookies: NormalizedCookie[];
}

const COOKIE_NAME_ALIASES: Record<string, string> = {
  "__client": "__client",
  "__client_uat": "__client_uat",
  "__client_uat_fqwayshe": "__client_uat_FQWayshe",
  "ddsession": "ddSession",
  "datadome": "datadome",
  "__cf_bm": "__cf_bm",
  "cf_clearance": "cf_clearance",
  "_cfuvid": "_cfuvid",
};

export function parseCookies(value: string): NormalizedCookie[] {
  const text = value.trim();
  if (!text) return [];
  if (text.startsWith("[") || text.startsWith("{")) {
    return normalizeCookieExport(JSON.parse(text) as unknown);
  }
  return normalizeCookieExport(
    text.split(";").map((part) => part.trim()).filter(Boolean).map((part) => {
      const separator = part.indexOf("=");
      if (separator <= 0) throw new Error(`无法解析 Cookie：${part}`);
      const name = part.slice(0, separator).trim();
      return {
        name,
        value: part.slice(separator + 1).trim(),
        domain: name.toLowerCase() === "__client" ? "clerk.higgsfield.ai" : ".higgsfield.ai",
        path: "/",
      };
    }),
  );
}

export function parseCookieJson(value: string): NormalizedCookie[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    throw new Error("Cookie 文件不是有效的 JSON");
  }
  return normalizeCookieExport(parsed);
}

/**
 * Parse either one browser cookie export or a bundle containing several
 * independent exports. Supported multi-account shapes are:
 *
 *   [{ "cookies": [...] }, { "cookies": [...] }]
 *   { "accounts": [{ "cookies": [...] }, ...] }
 *   [[...cookiesForAccountOne], [...cookiesForAccountTwo]]
 *
 * A normal root cookie array remains one account. Actual account identity is
 * deliberately not trusted from the file; the login worker discovers it from
 * the Clerk session.
 */
export function parseCookieJsonAccounts(value: string): ParsedCookieAccount[] {
  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    throw new Error("Cookie 文件不是有效的 JSON");
  }

  const entries = cookieAccountEntries(parsed);
  if (!entries.length) throw new Error("Cookie 文件中没有可导入的账号会话");
  if (entries.length > 100) throw new Error("单个 Cookie 文件最多包含 100 个账号会话");
  return entries.map((entry, index) => {
    try {
      if (Array.isArray(entry)) return { cookies: normalizeCookieExport(entry) };
      if (!entry || typeof entry !== "object") throw new Error("账号记录必须包含 cookies 数组");
      const record = entry as Record<string, unknown>;
      const label = String(record.label ?? record.name ?? record.email ?? "").trim();
      return {
        ...(label ? { label } : {}),
        cookies: normalizeCookieExport(record),
      };
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : String(caught);
      throw new Error(entries.length > 1 ? `第 ${index + 1} 个账号：${message}` : message);
    }
  });
}

function cookieAccountEntries(value: unknown): unknown[] {
  if (isCookieArray(value)) return [value];
  if (value && typeof value === "object" && !Array.isArray(value)) {
    const record = value as Record<string, unknown>;
    if (isCookieArray(record.cookies)) return [record];
    const accounts = record.accounts ?? record.sessions ?? record.profiles;
    if (Array.isArray(accounts)) return accounts;
  }
  if (Array.isArray(value)) return value;
  return [];
}

function isCookieArray(value: unknown): value is Array<Record<string, unknown>> {
  return Array.isArray(value)
    && value.length > 0
    && value.every((item) => Boolean(
      item
      && typeof item === "object"
      && !Array.isArray(item)
      && ("name" in item || "Name" in item)
      && ("value" in item || "Value" in item),
    ));
}

export function normalizeCookieExport(value: unknown): NormalizedCookie[] {
  const source = Array.isArray(value)
    ? value
    : value && typeof value === "object" && Array.isArray((value as Record<string, unknown>).cookies)
    ? (value as Record<string, unknown>).cookies as unknown[]
    : null;
  if (!source || source.some((item) => !item || typeof item !== "object" || Array.isArray(item))) {
    throw new Error("Cookies JSON 必须是对象数组或包含 cookies 数组的对象");
  }
  // Chrome exports include analytics, Google and consent cookies. Filter the
  // source before applying the API's bounded session-cookie limit, and accept
  // both Chrome's capitalized field names and the extension's camelCase form.
  const candidates = source.filter((item) => {
    if (!item || typeof item !== "object" || Array.isArray(item)) return false;
    const cookie = item as Record<string, unknown>;
    const rawName = String(cookie.name ?? cookie.Name ?? "").trim().toLowerCase();
    const rawDomain = String(cookie.domain ?? cookie.Domain ?? "").trim().toLowerCase().replace(/^\.+/, "");
    return Boolean(COOKIE_NAME_ALIASES[rawName]) && (
      rawDomain === "higgsfield.ai" || rawDomain.endsWith(".higgsfield.ai")
    );
  });
  if (candidates.length > 16) throw new Error("有效会话 Cookie 超过 16 条");
  const normalized = candidates.map((item, index): NormalizedCookie => {
    const cookie = item as Record<string, unknown>;
    const rawName = String(cookie.name ?? cookie.Name ?? "").trim().toLowerCase();
    const name = COOKIE_NAME_ALIASES[rawName];
    const cookieValue = String(cookie.value ?? cookie.Value ?? "");
    const domain = String(cookie.domain ?? cookie.Domain ?? "").trim();
    if (!name || !cookieValue || !domain) throw new Error(`第 ${index + 1} 条 Cookie 缺少 name、value 或 domain`);
    const sameSiteRaw = String(cookie.sameSite ?? cookie.SameSite ?? cookie.same_site ?? "unspecified").trim().toLowerCase();
    const sameSite = sameSiteRaw === "none" || sameSiteRaw === "no_restriction" || sameSiteRaw === "no-restriction"
      ? "no_restriction"
      : sameSiteRaw === "lax" || sameSiteRaw === "strict"
      ? sameSiteRaw
      : "unspecified";
    const expiration = Number(cookie.expirationDate ?? cookie.ExpirationDate ?? cookie.expiration_date ?? cookie.expires ?? cookie.Expires);
    return {
      name,
      value: cookieValue,
      domain,
      path: String(cookie.path ?? cookie.Path ?? "/"),
      secure: cookie.secure === undefined && cookie.Secure === undefined ? true : Boolean(cookie.secure ?? cookie.Secure),
      httpOnly: Boolean(cookie.httpOnly ?? cookie.HttpOnly ?? cookie.http_only),
      sameSite,
      ...(Number.isFinite(expiration) && expiration >= 0 ? { expirationDate: expiration } : {}),
    };
  });
  if (!normalized.some((cookie) => cookie.name.toLowerCase() === "__client")) {
    throw new Error("Cookie JSON 中必须包含 __client 会话");
  }
  return normalized;
}

export function cookieHeader(cookies: Array<Record<string, unknown>>, names: string[]): string {
  const allowed = new Set(names.map((name) => name.toLowerCase()));
  return cookies
    .filter((cookie) => allowed.has(String(cookie.name || "").toLowerCase()))
    .map((cookie) => `${String(cookie.name)}=${String(cookie.value)}`)
    .join("; ");
}
